import { Suspense, useEffect, useState, useMemo } from 'react';
import { connect } from 'react-redux';

import { Center, Spinner } from '@frontend/legacy-components';
import { constants } from '@frontend/partviewer';
import { encodeViewerPose } from '@frontend/partviewer/src/renderer/viewer-pose';

import { getPartInformation } from 'modules/viewerPage/data/reducer';

import partService from 'services/partService';

import { useCadViewerCamera } from './CadViewerCameraContext';
import PartViewer from './components/PartViewer';
import {
  cadViewer,
  crossSection,
  featureSnapTo,
  materialStock,
  measurementTool,
  pmiViewTool,
  selectorTool,
  viewerInteractions,
  viewerPartFileQueue,
} from './data/actions';
import { getMergedGeometricAnalysisData } from './data/geometricAnalysisData/reducer';
import {
  getActiveBodies,
  getActiveGeometricPartFileUuids,
  getCaliperObjects,
  getCaliperToolSelections,
  getCrossSectionEnabled,
  getCrossSectionInverted,
  getCrossSectionNormal,
  getCrossSectionPickMode,
  getCrossSectionPosition,
  getDisplayScale,
  getDisplayUnits,
  getDimensionalPrecision,
  getExplodedState,
  getFeatureSnapTo,
  getFocusedBodies,
  getForceFocus,
  getForceView,
  getHighlight,
  getHoverBody,
  getIsDimensionToolActive,
  getIsFocusedViewActive,
  getIsHighlightViewActive,
  getIsMeasurementToolActive,
  getIsOptimalBoundingBoxToolActive,
  getIsPMIViewActive,
  getIsSelectorToolActive,
  getIsWireFrameToolActive,
  getRenderNativeColors,
  getMaterialStockResults,
  getNativeUnits,
  getSelectedBodies,
  getSelectedObjects,
  getSelectorToolSelections,
  getShowAdditionalKeys,
  getShowAdditionalKeysExplicit,
  getViewerPendingPartFileUuids,
  getViewerState,
  getVisibleBodies,
  getVisibleStockResultKeys,
} from './data/reducer';

const { PART_VIEWER_MODES, ENVIRONMENT_TYPES } = constants;

const connector = connect(
  (state) => {
    const activeGeometricPartFileUuids = getActiveGeometricPartFileUuids(state);
    return {
      activeBodies: getActiveBodies(state),
      caliperObjects: getCaliperObjects(state),
      caliperToolSelections: getCaliperToolSelections(state),
      crossSectionEnabled: getCrossSectionEnabled(state),
      crossSectionInverted: getCrossSectionInverted(state),
      crossSectionNormal: getCrossSectionNormal(state),
      crossSectionPickMode: getCrossSectionPickMode(state),
      crossSectionPosition: getCrossSectionPosition(state),
      displayScale: getDisplayScale(state),
      displayUnits: getDisplayUnits(state),
      dimensionalPrecision: getDimensionalPrecision(state),
      explodedState: getExplodedState(state),
      featureSnapTo: getFeatureSnapTo(state),
      focusedBodies: getFocusedBodies(state),
      forceFocus: getForceFocus(state),
      forceView: getForceView(state),
      geometricAnalysisData: getMergedGeometricAnalysisData(
        state,
        activeGeometricPartFileUuids,
      ),
      highlight: getHighlight(state),
      hoverBody: getHoverBody(state),
      isDimensionToolActive: getIsDimensionToolActive(state),
      isFocusedViewActive: getIsFocusedViewActive(state),
      isHighlightViewActive: getIsHighlightViewActive(state),
      isMeasurementToolActive: getIsMeasurementToolActive(state),
      isOptimalBoundingBoxToolActive: getIsOptimalBoundingBoxToolActive(state),
      isPMIViewActive: getIsPMIViewActive(state),
      isSelectorToolActive: getIsSelectorToolActive(state),
      isWireFrameToolActive: getIsWireFrameToolActive(state),
      materialStockResults: getMaterialStockResults(state),
      nativeUnits: getNativeUnits(state),
      partInformation: getPartInformation(state),
      pendingPartFileUuids: getViewerPendingPartFileUuids(state),
      renderNativeColors: getRenderNativeColors(state),
      selectedBodies: getSelectedBodies(state),
      selectedObjects: getSelectedObjects(state),
      selectorToolSelections: getSelectorToolSelections(state),
      showAdditionalKeys: getShowAdditionalKeys(state),
      showAdditionalKeysExplicit: getShowAdditionalKeysExplicit(state),
      viewerState: getViewerState(state),
      visibleBodies: getVisibleBodies(state),
      visibleStockResultKeys: getVisibleStockResultKeys(state),
    };
  },
  {
    setCrossSectionNormal: crossSection.setNormal,
    setCrossSectionPickMode: crossSection.setPickMode,
    boxSelect: selectorTool.boxSelect,
    clearAllSelections: selectorTool.clearAllSelections,
    enableSelectorTool: selectorTool.enable,
    focusOnBodies: viewerInteractions.focusOnBodies,
    focusOnPartFile: viewerInteractions.focusOnPartFile,
    hideViewerBody: viewerInteractions.hideViewerBody,
    onLoadPartFile: viewerPartFileQueue.loadSuccess,
    onNestingCreationFinish: materialStock.finishNestingCreation,
    receiveCaliperObjects: measurementTool.receiveSelections,
    receiveFeatureSnapTo: featureSnapTo.request,
    receiveForceFocus: cadViewer.receiveForceFocus,
    receiveForceView: cadViewer.receiveForceView,
    receiveMeasurementComparison: measurementTool.receiveComparison,
    receiveMeasurementValue: measurementTool.receiveMeasurement,
    receiveMetaData: cadViewer.receiveMetaData,
    receiveHasPMIAnnotations: pmiViewTool.receiveHasPMIAnnotations,
    receiveSelectorToolCumulativeInfo: selectorTool.receiveCumulativeInfo,
    receiveSelectorToolSelection: selectorTool.receiveSelection,
    receiveShowDxfControls: cadViewer.receiveShowDxfControls,
    receiveVisibleBodies: cadViewer.receiveVisibleBodies,
    receiveTemporarilySimplified: cadViewer.receiveTemporarilySimplified,
    receiveTooComplexForPartViewer: cadViewer.receiveTooComplexForPartViewer,
    removeSelectorToolSelection: selectorTool.removeSelection,
    setReadyForInteraction: cadViewer.setInteractionReady,
    showAllComponents: viewerInteractions.showAllComponents,
    stockCreationFailed: materialStock.createFailed,
    stockCreationSuccess: materialStock.createSuccess,
  },
);

const CADViewer = ({
  activeBodies,
  pmiUrlByPartFileUuid,
  setCrossSectionNormal,
  setCrossSectionPickMode,
  boxSelect,
  caliperObjects,
  caliperToolSelections,
  clearAllSelections,
  crossSectionEnabled,
  crossSectionInverted,
  crossSectionNormal,
  crossSectionPickMode,
  crossSectionPosition,
  displayScale,
  displayUnits,
  dimensionalPrecision,
  enableSelectorTool,
  explodedState,
  featureSnapTo,
  focusedBodies,
  focusOnBodies,
  focusOnPartFile,
  forceFocus,
  forceView,
  geometricAnalysisData,
  hideViewerBody,
  highlight,
  hoverBody,
  isDimensionToolActive,
  isFocusedViewActive,
  isHighlightViewActive,
  isMeasurementToolActive,
  isOptimalBoundingBoxToolActive,
  isPaused, // passed in as direct prop
  isPMIViewActive,
  isSelectorToolActive,
  isWireFrameToolActive,
  renderNativeColors,
  leftOffset,
  materialStockResults,
  nativeUnits,
  onLoadPartFile,
  onNestingCreationFinish,
  partInformation,
  partViewerMode = PART_VIEWER_MODES.FULL, // passed in as direct prop
  pendingPartFileUuids,
  receiveCaliperObjects,
  receiveFeatureSnapTo,
  receiveForceFocus,
  receiveForceView,
  receiveHasPMIAnnotations,
  receiveMeasurementComparison,
  receiveMeasurementValue,
  receiveMetaData,
  receiveSelectorToolCumulativeInfo,
  receiveSelectorToolSelection,
  receiveShowDxfControls,
  receiveVisibleBodies,
  receiveTemporarilySimplified,
  receiveTooComplexForPartViewer,
  removeSelectorToolSelection,
  selectedBodies,
  selectedObjects,
  selectorToolSelections,
  setReadyForInteraction,
  showAdditionalKeys,
  showAdditionalKeysExplicit,
  showAllComponents,
  stockCreationFailed,
  stockCreationSuccess,
  viewerState,
  visibleBodies,
  visibleStockResultKeys,
}) => {
  const { registerViewerStateGetter } = useCadViewerCamera();
  const [forcedViewerPose, setForcedViewerPose] = useState('');
  const [acknowledgedForcedViewerPose, setAcknowledgedForcedViewerPose] =
    useState('');
  const [hasPartFileUuids, setHasPartFileUuids] = useState(false);

  const failedParts = useMemo(
    () =>
      partInformation.reduce(
        (output, { uuid, partFiles }) =>
          partFiles.find(
            (partFile) => partFile.isPrimaryFile && partFile.processingFailed,
          )
            ? (output.add(uuid), output)
            : output,
        new Set(),
      ),
    [partInformation],
  );

  useEffect(() => {
    if (forceFocus) {
      receiveForceFocus({ forceFocus: false });
    }
  }, [forceFocus, receiveForceFocus]);

  useEffect(() => {
    if (forceView) {
      receiveForceView({ forceView: null });
    }
  }, [forceView, receiveForceView]);

  useEffect(() => {
    if (featureSnapTo) {
      receiveFeatureSnapTo({ feature: null });
    }
  }, [featureSnapTo, receiveFeatureSnapTo]);

  useEffect(() => {
    if (acknowledgedForcedViewerPose === forcedViewerPose && forcedViewerPose) {
      setForcedViewerPose('');
      setAcknowledgedForcedViewerPose('');
    }
  }, [
    forcedViewerPose,
    acknowledgedForcedViewerPose,
    setForcedViewerPose,
    setAcknowledgedForcedViewerPose,
  ]);

  useEffect(() => {
    if (viewerState != null) {
      setForcedViewerPose(encodeViewerPose(viewerState, {}));
    }
  }, [viewerState, setForcedViewerPose]);

  // concat hover body to selected bodies, as its handled the same way
  const selectColorBodies = hoverBody
    ? selectedBodies.concat([hoverBody])
    : selectedBodies;

  useEffect(() => {
    if (!hasPartFileUuids && pendingPartFileUuids?.length > 0) {
      //Note: pendingPartFileUuids is reduced as partFileUuids are loaded
      //So we need to track if they were ever loaded. If so, PartViewer must be defined
      setHasPartFileUuids(true);
    }
  }, [hasPartFileUuids, pendingPartFileUuids]);

  // Local Suspense keeps the lazy `part_viewer` chunk (three.js) from bubbling
  // to the route-level SuspenseLoader and replacing the whole page.
  return hasPartFileUuids ? (
    <Suspense
      fallback={
        <Center height="100%">
          <Spinner size="xl" />
        </Center>
      }
    >
      <PartViewer
        activeBodies={activeBodies}
        pmiUrlByPartFileUuid={pmiUrlByPartFileUuid}
        activeCaliperTool={isMeasurementToolActive}
        activeSelectorTool={isSelectorToolActive}
        showAxisAlignedBoundingBox={isDimensionToolActive}
        caliperObjectsKeys={caliperObjects}
        caliperToolSelections={caliperToolSelections}
        clearAllSelections={clearAllSelections}
        crossSectionEnabled={crossSectionEnabled}
        crossSectionInverted={crossSectionInverted}
        crossSectionNormal={crossSectionNormal}
        crossSectionPickMode={crossSectionPickMode}
        crossSectionPosition={crossSectionPosition}
        displayScale={displayScale}
        displayUnits={displayUnits}
        dimensionalPrecision={dimensionalPrecision}
        enableSelectorTool={enableSelectorTool}
        env={
          process.env.NODE_ENV === 'development'
            ? ENVIRONMENT_TYPES.DEV
            : ENVIRONMENT_TYPES.PROD
        }
        explodedState={explodedState}
        failedParts={failedParts}
        featureSnapTo={featureSnapTo}
        // TODO: update this to be a bulk / have a cleaner way to fetch data - perhaps move it to a callback that gets passed in?
        //  https://paperlessparts.atlassian.net/browse/PP-20663
        fetchViewerData={partService.partFiles.viewerData.get}
        focusedBodies={focusedBodies}
        focusOnBodies={focusOnBodies}
        focusOnPartFile={focusOnPartFile}
        forcedViewerPose={forcedViewerPose}
        forceFocus={forceFocus}
        forceView={forceView}
        geometricAnalysisData={geometricAnalysisData} // THIS IS A STOP GAP THAT SHOULD BE GOOD TO SHIP TO PROD. CURRENTLY CONTAINS ALL OF THE GEOMETRIC ANALYSIS DATA (setups, feedbackItems, features, interrgoationFiles that were previously included in partModel and passed to the part viewer via the data prop)
        hideViewerBody={hideViewerBody}
        highlight={highlight}
        isFocusedViewActive={isFocusedViewActive}
        isPaused={isPaused}
        isPMIViewActive={isPMIViewActive}
        isSelectorToolActive={isSelectorToolActive}
        leftOffset={leftOffset}
        materialStockResults={materialStockResults}
        nativeUnits={nativeUnits}
        onCaliperObjectSelect={receiveCaliperObjects}
        onlyShowHighlighted={isHighlightViewActive}
        onLoadPartFile={onLoadPartFile}
        onNestingCreationFinish={onNestingCreationFinish}
        onNewMetaData={receiveMetaData}
        onNewMeasurement={receiveMeasurementValue}
        onNewMeasurementComparison={receiveMeasurementComparison}
        onNewSelectorCumulativeInfo={receiveSelectorToolCumulativeInfo}
        onCrossSectionNormalChange={setCrossSectionNormal}
        onCrossSectionPickModeChange={setCrossSectionPickMode}
        onSelectorObjectBoxSelect={boxSelect}
        onSelectorObjectDeselect={removeSelectorToolSelection}
        onSelectorObjectSelect={receiveSelectorToolSelection}
        pendingPartFileUuids={pendingPartFileUuids}
        receiveForceView={receiveForceView}
        receiveHasPMIAnnotations={receiveHasPMIAnnotations}
        receiveShowDxfControls={receiveShowDxfControls}
        receiveVisibleBodies={receiveVisibleBodies}
        registerViewerStateGetter={registerViewerStateGetter}
        receiveTemporarilySimplified={receiveTemporarilySimplified}
        receiveTooComplexForPartViewer={receiveTooComplexForPartViewer}
        renderNativeColors={renderNativeColors}
        selectedBodies={selectColorBodies}
        selectedObjectsKeys={selectedObjects}
        selectorToolSelections={selectorToolSelections}
        setAcknowledgedForcedViewerPose={setAcknowledgedForcedViewerPose}
        setReadyForInteraction={setReadyForInteraction}
        showAdditionalKeys={showAdditionalKeys}
        showAdditionalKeysExplicit={showAdditionalKeysExplicit}
        showAllComponents={showAllComponents}
        showHoverObject={isMeasurementToolActive || isSelectorToolActive}
        showOptimalBoundingBox={isOptimalBoundingBoxToolActive}
        showSelectedObjects={true}
        stockCreationFailed={stockCreationFailed}
        stockCreationSuccess={stockCreationSuccess}
        viewerMode={partViewerMode}
        visibleBodies={visibleBodies}
        visibleStockResultKeys={visibleStockResultKeys}
        wireframe={isWireFrameToolActive}
      />
    </Suspense>
  ) : null;
};

export default connector(CADViewer);
